import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, XCircle, ShieldCheck, Loader2, PlayCircle, Database } from 'lucide-react';

import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';

/**
 * CREDIT-SYSTEM CONCURRENCY PROOF
 * -------------------------------
 * Renders the verdict of the `credit-concurrency-test` edge function, which
 * fires genuinely simultaneous HTTP requests (separate connections => separate
 * database transactions) at the SAME function the marketplace uses to unlock a
 * lead. Nothing here is simulated in the browser: the button re-runs the suite
 * server-side, and every guarantee is enforced by row locks, a unique index,
 * CHECK constraints and a BEFORE INSERT capacity trigger inside Postgres.
 */

export interface ProofCase {
  key: string;
  title: string;
  proves: string;
  expected: string;
  actual: string;
  passed: boolean;
  detail?: { reasons?: string[]; refusals?: string[] };
}

export interface ProofAudit {
  check_key: string;
  passed: boolean;
  violations: number;
  detail: string;
}

export interface ProofRun {
  passed: boolean;
  total_cases: number;
  passed_cases: number;
  failed_cases: number;
  duration_ms: number;
  results: ProofCase[];
  audit: ProofAudit[];
  created_at?: string;
}

/** The five guarantees the suite exists to demonstrate. */
export const GUARANTEES = [
  {
    id: 1,
    en: 'A contractor can never access more leads than available credits',
    es: 'Un contratista nunca puede acceder a más leads que créditos disponibles',
    cases: ['multi_tab_multi_device', 'plan_allowance_exact', 'zero_credit_storm'],
  },
  {
    id: 2,
    en: 'Credits can never become negative',
    es: 'Los créditos nunca pueden quedar en negativo',
    cases: ['zero_credit_storm', 'multi_tab_multi_device', 'plan_allowance_exact'],
  },
  {
    id: 3,
    en: 'A lead can never exceed 6 contractors',
    es: 'Un lead nunca puede superar 6 contratistas',
    cases: ['lead_capacity_6', 'direct_insert_blocked'],
  },
  {
    id: 4,
    en: 'Duplicate requests cannot consume credits incorrectly',
    es: 'Las solicitudes duplicadas no pueden consumir créditos indebidamente',
    cases: ['double_click', 'replayed_request_idempotent'],
  },
  {
    id: 5,
    en: 'Every protection is enforced server-side / database-side',
    es: 'Todas las protecciones se aplican en el servidor y la base de datos',
    cases: ['direct_insert_blocked', 'buyer_cannot_be_spoofed'],
  },
] as const;

/** Last verdict seen by this browser, so the panel is never blank on return. */
const CACHE_KEY = 'gg_credit_proof_run';

const Verdict: React.FC<{ ok: boolean }> = ({ ok }) =>
  ok ? (
    <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
  ) : (
    <XCircle className="h-5 w-5 shrink-0 text-red-600" aria-hidden />
  );

const AdminConcurrencyProof: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const [run, setRun] = useState<ProofRun | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadLatest = useCallback(async () => {
    // Stored runs (mkt_concurrency_runs) are the durable record; the local
    // cache is only a fallback so the last verdict shows up instantly.
    const { data } = await supabase
      .from('mkt_concurrency_runs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1);
    if (data && data[0]) {
      setRun(data[0] as ProofRun);
      return;
    }
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) setRun(JSON.parse(cached) as ProofRun);
    } catch {
      /* ignore unreadable cache */
    }
  }, []);

  useEffect(() => {
    loadLatest();
  }, [loadLatest]);

  const runSuite = async () => {
    setBusy(true);
    setError('');
    try {
      const { data, error: err } = await supabase.functions.invoke('credit-concurrency-test', {
        body: {},
      });
      if (err || !data || (data as { error?: string }).error) {
        setError(es ? 'No se pudo ejecutar la prueba.' : 'The suite could not be run.');
      } else {
        const fresh = { ...(data as ProofRun), created_at: new Date().toISOString() };
        setRun(fresh);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(fresh));
        } catch {
          /* storage unavailable — the verdict is still on screen */
        }
      }
    } catch {
      setError(es ? 'No se pudo ejecutar la prueba.' : 'The suite could not be run.');
    } finally {
      setBusy(false);
    }
  };

  const caseFor = (key: string) => run?.results?.find((r) => r.key === key);

  return (
    <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-900">
            <ShieldCheck className="h-4 w-4 text-orange-500" />
            {es ? 'Prueba de concurrencia de créditos' : 'Credit system concurrency proof'}
          </h2>
          <p className="mt-1 max-w-3xl text-xs text-slate-500">
            {es
              ? 'Cada caso dispara peticiones HTTP realmente simultáneas contra la misma función que usa el marketplace para desbloquear un lead. Los bloqueos de fila, el índice único, las restricciones CHECK y el disparador de cupo se aplican dentro de la base de datos.'
              : 'Each case fires genuinely simultaneous HTTP requests at the same function the marketplace uses to unlock a lead. Row locks, the unique index, CHECK constraints and the capacity trigger are all enforced inside the database.'}
          </p>
        </div>
        <button
          onClick={runSuite}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-slate-800 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
          {busy ? (es ? 'Ejecutando…' : 'Running…') : es ? 'Ejecutar de nuevo' : 'Run the suite'}
        </button>
      </div>

      {error && (
        <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{error}</p>
      )}

      {!run ? (
        <p className="mt-6 text-sm text-slate-500">
          {es
            ? 'Todavía no hay ninguna ejecución. Pulsa “Ejecutar de nuevo”.'
            : 'No run recorded yet — press “Run the suite”.'}
        </p>
      ) : (
        <>
          <div
            className={`mt-5 flex flex-wrap items-center gap-3 rounded-xl px-4 py-3 ${
              run.passed ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
            }`}
          >
            <Verdict ok={run.passed} />
            <span className="text-sm font-extrabold">
              {run.passed
                ? es
                  ? 'TODAS LAS GARANTÍAS VERIFICADAS'
                  : 'ALL GUARANTEES VERIFIED'
                : es
                  ? 'FALLOS DETECTADOS'
                  : 'FAILURES DETECTED'}
            </span>
            <span className="text-xs font-semibold">
              {run.passed_cases}/{run.total_cases} {es ? 'casos' : 'cases'} · {run.duration_ms} ms
              {run.created_at ? ` · ${new Date(run.created_at).toLocaleString()}` : ''}
            </span>
          </div>

          <ul className="mt-5 grid gap-2 md:grid-cols-2">
            {GUARANTEES.map((g) => {
              const backing = g.cases.map(caseFor).filter(Boolean) as ProofCase[];
              const ok = backing.length > 0 && backing.every((c) => c.passed);
              return (
                <li key={g.id} className="flex items-start gap-2 rounded-xl border border-slate-200 px-3 py-2.5">
                  <Verdict ok={ok} />
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-800">
                      {g.id}. {es ? g.es : g.en}
                    </p>
                    <p className="truncate text-xs text-slate-400">{g.cases.join(' · ')}</p>
                  </div>
                </li>
              );
            })}
          </ul>

          <h3 className="mt-7 text-xs font-extrabold uppercase tracking-wide text-slate-500">
            {es ? 'Evidencia por caso' : 'Case-by-case evidence'}
          </h3>
          <div className="mt-3 space-y-2">
            {run.results?.map((c) => (
              <div key={c.key} className="rounded-xl border border-slate-200 px-4 py-3">
                <div className="flex items-start gap-2">
                  <Verdict ok={c.passed} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-900">{c.title}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{c.proves}</p>
                    <dl className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
                      <div>
                        <dt className="font-bold uppercase tracking-wide text-slate-400">
                          {es ? 'Esperado' : 'Expected'}
                        </dt>
                        <dd className="text-slate-600">{c.expected}</dd>
                      </div>
                      <div>
                        <dt className="font-bold uppercase tracking-wide text-slate-400">
                          {es ? 'Observado' : 'Observed'}
                        </dt>
                        <dd className={c.passed ? 'text-emerald-700' : 'text-red-700'}>{c.actual}</dd>
                      </div>
                    </dl>
                    {c.detail?.reasons && (
                      <p className="mt-2 break-words font-mono text-[11px] text-slate-400">
                        {c.detail.reasons.join(' | ')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <h3 className="mt-7 flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-slate-500">
            <Database className="h-3.5 w-3.5" />
            {es
              ? 'Invariantes verificadas sobre todos los datos reales'
              : 'Invariants audited across all live data'}
          </h3>
          <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full min-w-[560px] text-left text-sm">
              <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">{es ? 'Comprobación' : 'Check'}</th>
                  <th className="px-4 py-2.5">{es ? 'Infracciones' : 'Violations'}</th>
                  <th className="px-4 py-2.5">{es ? 'Aplicado por' : 'Enforced by'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {run.audit?.map((a) => (
                  <tr key={a.check_key}>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-2 font-semibold text-slate-800">
                        <Verdict ok={a.passed} />
                        {a.check_key}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-bold text-slate-900">{a.violations}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{a.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};

export default AdminConcurrencyProof;
